import React, { useState } from 'react'

import { Checkbox, Currency, RotatingChevronIcon, Space } from '../../atoms'
import { CheckboxAccordionItem } from '.'
import { ChildrenContainer, Container, Item, Label } from './styles'

interface Props {
  data: CheckboxAccordionItem[]
  isExpanded?: boolean
  isReadOnly?: boolean
  onChange?: (ids: string[], value: boolean) => void
}

export const CheckboxAccordion: React.FC<Props> = ({ data, isExpanded, isReadOnly, onChange }) => {
  const [state, setState] = useState<Record<string, boolean | 'indeterminate'>>(() => {
    // Initialize state from the data prop
    const initialState: Record<string, boolean | 'indeterminate'> = {}

    const processItems = (items: CheckboxAccordionItem[]) => {
      items.forEach(item => {
        // Set the initial state from the data prop
        if (item.checked !== undefined) {
          initialState[item.id] = item.checked
        }
        // Process children recursively
        if (item.children) {
          processItems(item.children)
        }
      })
    }
    processItems(data)
    return initialState
  })

  const [visibility, setVisibility] = useState<Record<string, boolean>>(() => {
    const initialVisibility: Record<string, boolean> = {}

    if (isExpanded) {
      const processItems = (items: CheckboxAccordionItem[]) => {
        items.forEach(item => {
          if (item.children && item.children.length > 0) {
            initialVisibility[item.id] = true
            processItems(item.children)
          }
        })
      }

      processItems(data)
    }

    return initialVisibility
  })

  const toggleVisibility = (id: string) => {
    setVisibility(prev => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  const updateState = (id: string, value: boolean | 'indeterminate') => {
    setState(prev => ({ ...prev, [id]: value }))
  }

  const setChildren = (items: CheckboxAccordionItem[], checked: boolean) => {
    items.forEach(item => {
      updateState(item.id, checked)
      if (item.children) setChildren(item.children, checked)
    })
  }

  const getChildrenState = (
    items: CheckboxAccordionItem[],
    currentState: Record<string, boolean | 'indeterminate'>
  ): boolean | 'indeterminate' => {
    const values = items.map(child => currentState[child.id])
    const allTrue = values.every(v => v === true)
    const allFalse = values.every(v => v === false)
    if (allTrue) return true
    if (allFalse) return false
    return 'indeterminate'
  }

  const handleParentChange = (item: CheckboxAccordionItem) => {
    const current = state[item.id] === true
    const newState = !current
    updateState(item.id, newState)
    if (item.children) {
      setChildren(item.children, newState)
    }
  }

  const handleChildChange = (parent: CheckboxAccordionItem, child: CheckboxAccordionItem) => {
    const current = state[child.id] === true
    const newChildState = !current
    const newState = {
      ...state,
      [child.id]: newChildState
    }

    updateState(child.id, newChildState)

    if (parent.children) {
      const newParentState = getChildrenState(parent.children, newState)
      updateState(parent.id, newParentState)
    }
  }

  const renderItem = (item: CheckboxAccordionItem, parent?: CheckboxAccordionItem, level = 0) => {
    const value = state[item.id] ?? false
    const hasChildren = item.children && item.children.length > 0
    const isVisible =
      level > 0 || visibility[item.id] !== undefined ? visibility[item.id] : isExpanded

    return (
      <React.Fragment key={item.id}>
        <Item
          hasChildren={hasChildren}
          highlighted={item.highlighted}
          isReadOnly={isReadOnly}
          isVisible={isVisible}
          level={level}
          onClick={() => hasChildren && level === 0 && toggleVisibility(item.id)}
        >
          <Label
            checked={!!value}
            isReadOnly={isReadOnly}
            onClick={e => {
              e.stopPropagation()
              if (isReadOnly || !onChange) return
              if (onChange) onChange([item.id], !value)
              if (parent) handleChildChange(parent, item)
              else handleParentChange(item)
            }}
          >
            <Checkbox
              aria-label={`${item.name} ${(() => {
                if (value === true) return 'checked'
                if (value === 'indeterminate') return 'indeterminate'
                return 'unchecked'
              })()}`}
              checked={value === true}
              data-testid={`checkbox-${item.id}`}
              disabled={isReadOnly}
              halfChecked={value === 'indeterminate'}
              onChange={() => {
                if (isReadOnly || !onChange) return
                if (onChange) onChange([item.id], !value)
                if (parent) handleChildChange(parent, item)
                else handleParentChange(item)
              }}
            />
            {item.name}
          </Label>
          <Space isflex alignItems="center" style={{ gap: '0.5rem' }}>
            {item.value !== undefined && <Currency value={item.value} />}
            {hasChildren ? (
              <RotatingChevronIcon isRotated={isVisible} rotationDegrees="180" />
            ) : (
              <div style={{ width: '20px' }} />
            )}
          </Space>
        </Item>
        {hasChildren && (
          <ChildrenContainer isVisible={isVisible}>
            {item.children?.map(child => renderItem(child, item, level + 1))}
          </ChildrenContainer>
        )}
      </React.Fragment>
    )
  }

  return <Container>{data.map(item => renderItem(item))}</Container>
}
